import { CancelToken } from 'axios';
import { MEMBERS_WEB_APP_URL, SCHEDULE_API_BASE_URL } from 'config';
import moment from 'moment';
import { appointmentsClient } from 'node-api/appointments/client';
import { SharedEnaraNodeClient } from 'node-api/common/SharedEnaraNodeClient';
import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { EventNames } from 'services/mixpanel/provider-app-mixpanel-events';
import { BackgroundEvent } from 'services/mixpanel/shared-mixpanel-service';
import { debug } from 'utils/helpers';
import {
  AppointmentSummary,
  AppointmentSummaryScope,
  Attendee,
  GetAppointmentsSummaryResponse,
  SanitizedCoreAppointment,
  SanitizedCoreAppointmentResponse,
} from './types.v3';

class SchedulingV3 extends SharedEnaraNodeClient {
  public async listAppointments(
    {
      memberId,
    }: {
      memberId: number;
    },
    cancelToken?: CancelToken
  ): Promise<SanitizedCoreAppointment[]> {
    const listResponse = await this.axiosClient.get<SanitizedCoreAppointmentResponse>(
      `/v3/members/${memberId}/appointments`,
      { cancelToken }
    );

    return listResponse?.data?.appointments || [];
  }

  public async getProviderVideoUrl(
    memberInfo: { id: number; uuid: string },
    providerInfo: { id: number; uuid: string },
    tokenFor: Attendee, // To validate later in member-web-app
    calledFrom: string,
    coreScheduleId?: number
  ): Promise<string> {
    if (providerInfo.uuid.trim().length === 0) {
      throw new Error('Unable to get provider video url');
    }

    try {
      MixpanelWrapperInstance.track(EventNames.MemberGetCallLink, {
        targetLabel: 'getProviderVideoUrl',
        targetType: BackgroundEvent.CallingService,
        memberUuid: memberInfo.uuid,
        providerUuid: providerInfo.uuid,
        calledFrom,
        source: 'get-provider-url',
        videoVersion: 'v2',
      });

      /**
       * TODO: this call should be removed from here.
       * One service client can't be calling another service client
       */
      const meetingData = await appointmentsClient.getVideoMeetingDataWithAppointmentId(
        coreScheduleId!
      );

      const meetingParticipant = meetingData.participants.find((participant) => {
        if (tokenFor === 'member' || tokenFor === 'guest') {
          return participant.userUuid === memberInfo.uuid;
        }

        return participant.userUuid === providerInfo.uuid;
      });

      if (!meetingParticipant?.token) {
        throw new Error('Unable to get video token');
      }

      const videoToken = meetingParticipant.token;

      return `${MEMBERS_WEB_APP_URL}/meetings/${videoToken}?source=${calledFrom}`;
    } catch (error) {
      console.error('getProviderVideoUrl::error: ', error);

      throw new Error('Unable to get provider video url');
    }
  }

  public async sendEmailVideoLinkReminder(memberUuid: string, providerUuid: string) {
    const nowForceTime = moment().format('YYYY/MM/DD hh:mm');

    const url = `/v1/providers/${providerUuid}/members/${memberUuid}/appointments/next/reminders/email?force=true&appointmentTime=${nowForceTime}`;
    try {
      await this.axiosClient.post(url);

      return true;
    } catch (err) {
      console.error('emailVideoLinkReminder::error: ', err);
      return false;
    }
  }

  public async getProviderAppointmentsSummary(
    page: number,
    appointmentsScope: AppointmentSummaryScope,
    locationType: 'OL' | 'IP' = 'OL'
  ): Promise<{ appointments: AppointmentSummary[]; totalPages: number } | null> {
    try {
      const response = await this.axiosClient.get<GetAppointmentsSummaryResponse>(
        '/v1/providers/me/appointments/summary',
        { params: { page, appointmentsScope, locationType } }
      );

      const { appointments, totalPages } = response.data;

      return { appointments, totalPages };
    } catch (error) {
      debug(error as string);
    }

    return null;
  }
}

export const schedulingV3Client = new SchedulingV3(SCHEDULE_API_BASE_URL);
